import { useEffect, useCallback } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { ContentTitleBar } from "../../../components/ContentTitleBar";
import { useUser } from "../../../store/user/hooks";
import { useUsers } from "../../../store/users/hooks";
import { PasswordField } from "./PasswordField";
import { WaitDialog } from "../../../components/dialogs";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { UserSchema, UserDefaultValues } from "./UserShared";

export const EditUserPage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const {
    loadUsers,
    list,
    isLoading: usersIsLoading,
    reset: usersReset,
  } = useUsers();
  const {
    isDeleted,
    isDeleting,
    isLoading,
    isSaved,
    isSaving,
    user,
    deleteUser,
    forceLogoutUser,
    loadUser,
    saveUser,
  } = useUser();

  const showWaitDialog = isDeleting || isLoading || isSaving || usersIsLoading;

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid },
    reset: rhfReset,
    trigger,
    watch,
  } = useForm({
    resolver: zodResolver(UserSchema(user, list)),
    values: UserDefaultValues(user) || {},
    mode: "all",
  });

  const onSubmit = async (data) => {
    await saveUser({ ...user, ...data });
    rhfReset();
    loadUsers();
  };

  const cancelChanges = useCallback(async () => {
    await usersReset();
    rhfReset();
    navigate("/app/admin/users");
  }, [usersReset, navigate, rhfReset]);

  useEffect(() => {
    if (!userId && user.id) {
      navigate(`/app/admin/user/${user.id}`);
    }
  }, [userId, user, navigate]);

  useEffect(() => {
    if (isDeleted) {
      cancelChanges();
    }
  }, [isDeleted, isSaved, cancelChanges]);

  useEffect(() => {
    if (userId) loadUser(userId);
    loadUsers();
  }, [userId, loadUser, loadUsers]);

  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name === "password") {
        trigger("confirmPassword");
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  return (
    <Stack component="main" height={1}>
      <WaitDialog open={showWaitDialog} />
      <Stack
        flex={1}
        m="auto"
        maxHeight="85vh"
        maxWidth="800px"
        pb={8}
        px={2}
        sx={{ overflowY: "scroll" }}
        width={1}
      >
        <Grid
          component="form"
          container
          display="flex"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          spacing={2}
          width={1}
        >
          <Grid my={2} size={12}>
            <Typography variant="h5">Personal Details</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={!!errors.firstName}
                  fullWidth
                  helperText={
                    errors.firstName
                      ? errors.firstName.message
                      : "Please enter a first name."
                  }
                  label="First Name"
                  required
                  slotProps={{ htmlInput: { tabIndex: 1 } }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={!!errors.lastName}
                  fullWidth
                  helperText={
                    errors.lastName
                      ? errors.lastName.message
                      : "Please enter a last name."
                  }
                  label="Last Name"
                  required
                  slotProps={{ htmlInput: { tabIndex: 2 } }}
                />
              )}
            />
          </Grid>
          <Grid size={12}>
            <Controller
              name="organization"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  helperText="Please enter an organization."
                  label="Organization"
                  slotProps={{ htmlInput: { tabIndex: 3 } }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={!!errors.email}
                  fullWidth
                  helperText={
                    errors.email
                      ? errors.email.message
                      : "Please enter a valid email."
                  }
                  label="Email"
                  required
                  slotProps={{ htmlInput: { tabIndex: 4 } }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  helperText="Please enter a valid phone number."
                  label="Mobile"
                  slotProps={{ htmlInput: { tabIndex: 5 } }}
                />
              )}
            />
          </Grid>
          <Grid display="flex" gap={4} my={2} size={12}>
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      {...field}
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      slotProps={{ input: { tabIndex: 6 } }}
                    />
                  }
                  label="Is Enabled"
                  labelPlacement="start"
                  sx={{ ml: 0.5 }}
                />
              )}
            />
            <Controller
              name="roles"
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <FormControlLabel
                  label="Is Admin"
                  labelPlacement="start"
                  sx={{ ml: 0.5 }}
                  control={
                    <Switch
                      {...field}
                      checked={value.includes("ADMIN")}
                      onChange={(e) =>
                        onChange(e.target.checked ? ["ADMIN"] : ["USER"])
                      }
                      slotProps={{ input: { tabIndex: 7 } }}
                    />
                  }
                />
              )}
            />
          </Grid>
          <Grid size={12}>
            <Typography variant="caption">
              Leave password blank unless changing the user's password.
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <PasswordField
              name="password"
              label="Password"
              control={control}
              helperText="Please enter a password."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <PasswordField
              name="confirmPassword"
              label="Confirm Password"
              control={control}
              helperText="Please confirm your password."
            />
          </Grid>
          <Grid mt={2} size={12}>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
              <Button
                variant="contained"
                disabled={!isValid || !isDirty}
                tabIndex={11}
                type="submit"
                sx={{
                  minWidth: 0,
                  width: "fit-content",
                }}
              >
                Update User
              </Button>
              <Button
                variant="outlined"
                disabled={!isDirty}
                onClick={() => rhfReset()}
                sx={{
                  minWidth: 0,
                  width: "fit-content",
                  mr: { xs: 0, sm: "auto" },
                }}
                tabIndex={12}
              >
                Clear
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={async () => {
                  await deleteUser(userId, false);
                  navigate("/app/admin/users");
                }}
                sx={{ minWidth: 0, width: "fit-content" }}
                tabIndex={13}
              >
                Delete User
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={async () => await deleteUser(userId, true)}
                sx={{ minWidth: 0, width: "fit-content" }}
                tabIndex={14}
              >
                Delete Cases
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={async () => await forceLogoutUser(userId)}
                sx={{ minWidth: 0, width: "fit-content" }}
                tabIndex={15}
              >
                Force Logout
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Stack>
      <Box position="fixed" bottom={0} width={1} zIndex={1}>
        <ContentTitleBar onBack={() => cancelChanges()} label="Edit User" />
      </Box>
    </Stack>
  );
};
